<template>
  <div v-if="text || $slots.default" class="n-tooltip">
    <div
      class="n-tooltip__trigger"
      :class="{ icon: isIconMode }"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      ref="tooltipHost"
    >
      <slot>
        <span class="n-tooltip__icon">
          <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
            <path d="M0 0h24v24H0z" fill="none" />
            <path
              d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"
            />
          </svg>
        </span>
      </slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, onBeforeUnmount, computed, useSlots, PropType } from 'vue';
import { createApp } from 'vue';
import NTooltipContent from './tooltip-content.vue';
import { createAppWithPlugins } from '@/utils/createAppWithPlugins';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface TooltipContentInstance {
  hidden: boolean;
  position: TooltipPosition;
  calculatePosition(hostPosition: DOMRect): void;
  remove(): void;
  content: any;
}

defineOptions({
  name: 'n-tooltip',
});

// Define props.
const props = defineProps({
  text: {
    type: String,
    default: '',
  },
  position: {
    type: String as PropType<TooltipPosition>,
    default: 'top',
  },
  offset: {
    type: Number,
    default: undefined,
  },
  wide: {
    type: Boolean,
    default: false,
  },
});

// Refs for the host element and the tooltip.
const tooltipHost = ref<HTMLDivElement | null>(null);
const tooltipElement = ref<TooltipContentInstance | null>(null);
const tooltipContentHost = ref<HTMLDivElement | null>(null);
let tooltipApp: ReturnType<typeof createApp> | null = null;

const slots = useSlots();

const hasTooltipContent = computed(() => !!props.text || !!slots.message);
const isIconMode = computed(() => !slots.default);

watch(
  [() => props.text, () => slots.message],
  () => {
    if (tooltipElement.value) {
      cleanupTooltip();
    }
    if (hasTooltipContent.value) {
      generateTooltipContentElement();
    }
  },
  { deep: true },
);

watch(
  () => props.position,
  () => {
    if (tooltipElement.value) {
      tooltipElement.value.position = props.position;
      calculateTooltipPosition();
    }
  },
);

onMounted(() => {
  if (hasTooltipContent.value) {
    generateTooltipContentElement();
  }
});

onBeforeUnmount(() => {
  if (tooltipApp) {
    tooltipApp.unmount();
    tooltipApp = null;
  }
  cleanupTooltip();
});

function handleMouseEnter() {
  if (!hasTooltipContent.value || !tooltipElement.value) return;
  calculateTooltipPosition();
  tooltipElement.value.hidden = false;
}

function handleMouseLeave() {
  if (tooltipElement.value) {
    tooltipElement.value.hidden = true;
  }
}

function calculateTooltipPosition() {
  if (!tooltipHost.value || !tooltipElement.value) return;
  const hostPosition = tooltipHost.value.getBoundingClientRect();
  tooltipElement.value.calculatePosition(hostPosition);
}

function generateTooltipContentElement() {
  tooltipContentHost.value = document.createElement('div');
  document.body.appendChild(tooltipContentHost.value);

  let offset = props.offset;
  if (offset === undefined && isIconMode.value) {
    offset = -6;
  }

  tooltipApp = createAppWithPlugins(NTooltipContent, {
    content: generateTooltipContent(),
    position: props.position,
    iconOffsetPx: offset,
    wide: props.wide,
  });

  const instance = tooltipApp.mount(tooltipContentHost.value);
  tooltipElement.value = instance as unknown as TooltipContentInstance;
}

function generateTooltipContent() {
  const messageSlot = slots.message ? slots.message() : [];
  return messageSlot.length > 0 ? messageSlot : props.text;
}

function cleanupTooltip() {
  if (tooltipElement.value && typeof tooltipElement.value.remove === 'function') {
    tooltipElement.value.remove();
    tooltipElement.value = null;
  }
  if (tooltipContentHost.value && tooltipContentHost.value.parentNode) {
    document.body.removeChild(tooltipContentHost.value);
    tooltipContentHost.value = null;
  }
}
</script>

<style lang="scss" scoped>
$n-tooltip-icon-offset: 0.125em;

.n-tooltip {
  display: inline-block;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
      height: 1em;
      width: 1em;
      fill: currentColor;
    }
  }

  &__trigger {
    position: relative;

    &.icon .n-tooltip__icon {
      position: absolute;
      bottom: -$n-tooltip-icon-offset;
    }
  }
}
</style>
